/**
 * Device detection utility for analytics/meta tracking.
 * Uses browser APIs with progressive enhancement:
 *   1. User-Agent Client Hints (Chrome/Edge 90+)
 *   2. UA string parsing (universal fallback)
 *   3. Touch/pointer/screen heuristics for device type
 */

export type DeviceType = 'phone' | 'tablet' | 'desktop'
export type DevicePlatform = 'ios' | 'android' | 'macos' | 'windows' | 'linux' | 'chromeos' | 'unknown'

export interface DeviceInfo {
    /** Physical device form factor */
    deviceType: DeviceType
    /** Operating system platform */
    platform: DevicePlatform
}

interface NavigatorUAData {
    mobile: boolean
    platform: string
}

/**
 * Detects the platform (OS) the browser is running on.
 * Prefers Client Hints API, falls back to UA string.
 */
function detectPlatform(): DevicePlatform {
    // Client Hints API (Chrome/Edge 90+)
    if ('userAgentData' in navigator) {
        const uaData = navigator.userAgentData as NavigatorUAData | undefined
        if (uaData?.platform) {
            const platform = uaData.platform.toLowerCase()
            if (platform === 'android') return 'android'
            if (platform === 'ios') return 'ios'
            if (platform === 'macos' || platform === 'mac os x') return 'macos'
            if (platform === 'windows') return 'windows'
            if (platform === 'linux') return 'linux'
            if (platform === 'chrome os' || platform === 'chromeos') return 'chromeos'
        }
    }

    // Fallback: UA string parsing
    const ua = navigator.userAgent
    if (/android/i.test(ua)) return 'android'
    if (/iPad|iPhone|iPod/.test(ua) || (/Macintosh/i.test(ua) && navigator.maxTouchPoints > 1)) return 'ios'
    if (/Macintosh|Mac OS X/i.test(ua)) return 'macos'
    if (/Windows/i.test(ua)) return 'windows'
    if (/CrOS/i.test(ua)) return 'chromeos'
    if (/Linux/i.test(ua)) return 'linux'

    return 'unknown'
}

/**
 * Detects the device form factor (phone, tablet, desktop).
 * Combines multiple signals for accuracy:
 *   - Client Hints mobile flag
 *   - Touch capability + pointer type
 *   - Screen dimensions for tablet vs phone distinction
 */
function detectDeviceType(platform: DevicePlatform): DeviceType {
    // Client Hints mobile flag (Chrome/Edge 90+)
    // Note: tablets report mobile=false in Client Hints
    let clientHintsMobile: boolean | undefined
    if ('userAgentData' in navigator) {
        const uaData = navigator.userAgentData as NavigatorUAData | undefined
        if (uaData) {
            clientHintsMobile = uaData.mobile
        }
    }

    const isTouch = navigator.maxTouchPoints > 0
    const isCoarsePointer = window.matchMedia?.('(pointer: coarse)').matches ?? false

    // Desktop: no touch capability or fine pointer only
    if (!isTouch && !isCoarsePointer) {
        return 'desktop'
    }

    // If Client Hints says mobile=true, it's a phone (tablets report false)
    if (clientHintsMobile === true) {
        return 'phone'
    }

    // Touch device — distinguish tablet from phone via screen size
    // Use the smaller dimension to handle both orientations
    const screenMin = Math.min(screen.width, screen.height)

    // iPadOS detection: reports as macOS but has touch
    if (platform === 'macos' && isTouch) {
        return 'tablet'
    }

    // Android/iOS touch devices: use 600px breakpoint (standard tablet threshold)
    if (platform === 'android' || platform === 'ios') {
        return screenMin >= 600 ? 'tablet' : 'phone'
    }

    // Touch-capable Windows/ChromeOS (Surface, Chromebook) — treat as desktop
    // unless screen is small enough to be a phone/tablet
    if (isCoarsePointer && screenMin < 600) {
        return 'phone'
    }
    if (isCoarsePointer && screenMin < 1024) {
        return 'tablet'
    }

    return 'desktop'
}

/**
 * Returns device type and platform information using browser APIs.
 * Synchronous — safe to call in any context (no async, no permissions).
 * Wrapped in try/catch so detection failures never block the caller.
 *
 * @example
 * const info = getDeviceInfo()
 * // { deviceType: 'phone', platform: 'ios' }
 */
export function getDeviceInfo(): DeviceInfo {
    try {
        const platform = detectPlatform()
        const deviceType = detectDeviceType(platform)
        return { deviceType, platform }
    } catch {
        return { deviceType: 'desktop', platform: 'unknown' }
    }
}
